import { useOne } from "@refinedev/core";
import { Flex, Spin, Typography } from "antd";
import { useAntTheme } from "hooks/useAntTheme/";
import { styled } from "styled-components";
import { FaceProfileResponse } from "../../types";
import { AVATAR_ITEM_PREVIEW_BORDER_RADIUS } from "./AvatarDetails";
import { AvatarPreviewSelectButton } from "./AvatarPreviewSelectButton";
import { getFaceProfileImagePath } from "./helpers";
import { AvatarImage } from "./AvatarImage";

interface AvatarFacePreviewProps {
  id: string;
  assetPath?: string;
  name: string;
  onClick: () => void;
  ghost?: boolean;
}

export const AvatarFacePreview = ({
  id,
  assetPath,
  name,
  ghost,
  onClick,
}: AvatarFacePreviewProps) => {
  const { theme } = useAntTheme();

  const { data } = useOne<FaceProfileResponse>({
    resource: `media/face_profiles`,
    id,
    queryOptions: {
      enabled: !!id && !assetPath,
      refetchInterval(data) {
        // check until we have a path to show
        if (data?.data.category === "Premade") {
          return false;
        }
        if (data?.data.extracted_asset_path || data?.data.custom_asset_path) {
          return false;
        }
        return 3000;
      },
    },
    errorNotification() {
      return false;
    }, // hide errors
  });
  const isProcessing = ghost || (!data?.data && !assetPath);
  const path = getFaceProfileImagePath(data?.data) ?? assetPath;

  return (
    <Flex
      vertical
      justify="space-between"
      align="center"
      style={{
        width: "100%",
        aspectRatio: "1 / 0.9",
        borderRadius: AVATAR_ITEM_PREVIEW_BORDER_RADIUS,
        overflow: "hidden",
        border: `1px solid ${theme.colorBorderSecondary}`,
        padding: "8px 10px",
      }}
      gap={7}
      onClick={onClick}
      data-testid="avatar-face-preview-container"
    >
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          flex: 1,
          minHeight: 0,
          alignItems: "center",
        }}
      >
        {isProcessing ? (
          <StyledSpin spinning={isProcessing} />
        ) : (
          <AvatarImage src={path} alt={name} width={"100%"} height={"100%"} />
        )}
      </div>
      <Flex
        justify="space-between"
        align="center"
        style={{ width: "100%", flexShrink: 0 }}
        gap={5}
      >
        <Typography.Text
          style={{
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: -1,
            lineHeight: 1.1,
          }}
        >
          {name}
        </Typography.Text>
        <AvatarPreviewSelectButton onClick={onClick} />
      </Flex>
    </Flex>
  );
};

const StyledSpin = styled(Spin)`
  height: unset !important;
`;
